import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiFetch } from "../../utils";

export const FIELD_TYPES = [
    { value: 0, label: "Text" },
    { value: 1, label: "Number" },
    { value: 2, label: "Boolean" },
    { value: 3, label: "Secret" },
    { value: 4, label: "Email" },
    { value: 5, label: "URL" },
    { value: 6, label: "Textarea" },
    { value: 7, label: "Select" },
];
/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

const clone = (data) =>
    JSON.parse(JSON.stringify(data));

const createId = () =>
    `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;

const createEmptyField = () => ({
    id: createId(),
    type: "text",
    required: false,
    placeholder: "",
    description: "",
    options: [],
});

const createEmptyConfiguration = () => ({
    id: "new",
    name: "New Configuration",
    version: 1,
    fields: [],
});

/*
|--------------------------------------------------------------------------
| Fetch Configurations
|--------------------------------------------------------------------------
|
| MOCK FOR NOW
|
*/

export const fetchConfigurations = createAsyncThunk(
    "configs/fetchConfigurations",
    async () => {
        const response = await apiFetch(`api/c`);
        return await response.json();
    }
);

export const saveConfig = createAsyncThunk(
    "configs/createConfiguration",
    async (payload) => {
        const response = await apiFetch(`api/c`, {
            method: "POST",
            body: JSON.stringify(payload),
            idempotencyAction: "config:create"
        });
        if (!response.ok) throw new Error("Config Creation Failed");
        return await response.json();
    }
);

export const updateConfig = createAsyncThunk(
    "configs/updateConfig",
    async (payload) => {
        const response = await apiFetch(`api/c/${payload.id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
            idempotencyAction: "config:update"
        });
        if (!response.ok) throw new Error("Config Creation Failed");
        return await response.json();
    }
);

/*
|--------------------------------------------------------------------------
| Delete Configuration
|--------------------------------------------------------------------------
*/

export const deleteConfig = createAsyncThunk(
    "configs/deleteConfig",
    async (payload) => {
        const response = await apiFetch(`api/c/${payload}`, {
            method: "DELETE",
        });
        if (!response.ok) throw new Error("Config Deletion Failed");
        return await response.json();
    }
);
/*
|--------------------------------------------------------------------------
| Slice
|--------------------------------------------------------------------------
*/

const configSlice = createSlice({
    name: "config",

    initialState: {
        configurations: [],
        currentConfigId: null,
        currentConfig:null,
        expandedFieldId: null,

        loading: false,
        saving: false,
        error: null,
    },

    reducers: {
        /*
        |--------------------------------------------------------------------------
        | Configuration
        |--------------------------------------------------------------------------
        */

        setCurrentConfig: (state, action) => {
            const config =
                state.configurations.find(
                    (item) =>
                        item.id === action.payload
                );

            if (!config) {
                state.currentConfigId = null;
                state.currentConfig = null;
                return;
            }

            state.currentConfigId = config.id;
            state.currentConfig = clone(config);
            state.expandedFieldId = null;
        },

        createConfiguration: (state) => {
            const config =
                createEmptyConfiguration();

            state.configurations.push(config);

            state.currentConfigId = config.id;
            state.currentConfig = clone(config);
            state.expandedFieldId = null;
        },

        updateConfigName: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            state.currentConfig.name =
                action.payload;
        },

        /*
        |--------------------------------------------------------------------------
        | Fields
        |--------------------------------------------------------------------------
        */

        addField: (state) => {
            if (!state.currentConfig) {
                return;
            }

            const field =
                createEmptyField();

            state.currentConfig.fields.push(field);

            state.expandedFieldId = field.id;
        },

        updateField: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            const {
                fieldId,
                key,
                value,
            } = action.payload;

            const field =
                state.currentConfig.fields.find(
                    (item) =>
                        item.id === fieldId
                );

            if (!field) {
                return;
            }

            if (key === "keyType") {
                field[key] = parseInt(value,8);
            } else {
                field[key] = value;
            }
        },

        deleteField: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            state.currentConfig.fields =
                state.currentConfig.fields.filter(
                    (field) =>
                        field.id !== action.payload
                );

            if (
                state.expandedFieldId ===
                action.payload
            ) {
                state.expandedFieldId = null;
            }
        },

        moveField: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            const {
                fieldId,
                direction,
            } = action.payload;

            const fields =
                state.currentConfig.fields;

            const index =
                fields.findIndex(
                    (field) =>
                        field.id === fieldId
                );

            if (index === -1) {
                return;
            }

            const target =
                direction === "up"
                    ? index - 1
                    : index + 1;

            if (
                target < 0 ||
                target >= fields.length
            ) {
                return;
            }

            [
                fields[index],
                fields[target],
            ] = [
                    fields[target],
                    fields[index],
                ];
        },

        toggleField: (state, action) => {
            state.expandedFieldId =
                state.expandedFieldId ===
                    action.payload
                    ? null
                    : action.payload;
        },

        /*
        |--------------------------------------------------------------------------
        | Select Options
        |--------------------------------------------------------------------------
        */

        addOption: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            const field =
                state.currentConfig.fields.find(
                    (item) =>
                        item.id === action.payload
                );

            if (!field) {
                return;
            }

            if (!field.options) {
                field.options = [];
            }

            field.options.push({
                key: "",
                value: "",
            });
        },

        updateOption: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            const {
                fieldId,
                optionIndex,
                key,
                value,
            } = action.payload;

            const field =
                state.currentConfig.fields.find(
                    (item) =>
                        item.id === fieldId
                );

            if (!field?.options?.[optionIndex]) {
                return;
            }

            field.options[optionIndex][key] =
                value;
        },

        deleteOption: (state, action) => {
            if (!state.currentConfig) {
                return;
            }

            const {
                fieldId,
                optionIndex,
            } = action.payload;

            const field =
                state.currentConfig.fields.find(
                    (item) =>
                        item.id === fieldId
                );

            if (!field?.options) {
                return;
            }

            field.options.splice(
                optionIndex,
                1
            );
        },

        /*
        |--------------------------------------------------------------------------
        | Reset
        |--------------------------------------------------------------------------
        */

        resetCurrentConfig: (state) => {
            const config =
                state.configurations.find(
                    (item) =>
                        item.id ===
                        state.currentConfigId
                );

            if (!config) {
                return;
            }

            state.currentConfig = clone(config);
            state.expandedFieldId = null;
        },
    },

    /*
    |--------------------------------------------------------------------------
    | Async
    |--------------------------------------------------------------------------
    */

    extraReducers: (builder) => {
        builder

            // Fetch
            .addCase(
                fetchConfigurations.pending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )

            .addCase(
                fetchConfigurations.fulfilled,
                (state, action) => {
                    state.configurations =
                        action.payload;

                    state.loading = false;
                }
            )

            .addCase(
                fetchConfigurations.rejected,
                (state, action) => {
                    state.loading = false;
                    state.error =
                        action.error.message;
                }
            )

            // Save
            .addCase(
                saveConfig.pending,
                (state) => {
                    state.saving = true;
                    state.error = null;
                }
            )

            .addCase(
                saveConfig.fulfilled,
                (state, action) => {
                    const saved =
                        action.payload;

                    const index =
                        state.configurations.findIndex(
                            (item) =>
                                item.id ===
                                saved.id
                        );

                    if (index >= 0) {
                        state.configurations[
                            index
                        ] = clone(saved);
                    } else {
                        state.configurations.push(
                            clone(saved)
                        );
                    }

                    state.currentConfig =
                        clone(saved);

                    state.currentConfigId =
                        saved.id;

                    state.saving = false;
                }
            )

            .addCase(
                saveConfig.rejected,
                (state, action) => {
                    state.saving = false;
                    state.error =
                        action.error.message;
                }
            )
            // update
            .addCase(
                updateConfig.pending,
                (state) => {
                    state.saving = true;
                    state.error = null;
                }
            )

            .addCase(
                updateConfig.fulfilled,
                (state, action) => {
                    const saved =
                        action.payload;

                    const index =
                        state.configurations.findIndex(
                            (item) =>
                                item.id ===
                                saved.id
                        );

                    if (index >= 0) {
                        state.configurations[
                            index
                        ] = clone(saved);
                    } else {
                        state.configurations.push(
                            clone(saved)
                        );
                    }

                    state.currentConfig =
                        clone(saved);

                    state.currentConfigId =
                        saved.id;

                    state.saving = false;
                }
            )

            .addCase(
                updateConfig.rejected,
                (state, action) => {
                    state.saving = false;
                    state.error =
                        action.error.message;
                }
            )

            // Delete
            .addCase(
                deleteConfig.fulfilled,
                (state, action) => {
                    const index = state.configurations.findIndex(
                        c => c.id === action.payload
                    );

                    if (index !== -1) {
                        state.configurations.splice(index, 1);
                    }
                }
            );
    },
});

/*
|--------------------------------------------------------------------------
| Actions
|--------------------------------------------------------------------------
*/

export const {
    setCurrentConfig,
    createConfiguration,
    updateConfigName,

    addField,
    updateField,
    deleteField,
    moveField,
    toggleField,

    addOption,
    updateOption,
    deleteOption,

    resetCurrentConfig,
} = configSlice.actions;

/*
|--------------------------------------------------------------------------
| Selectors
|--------------------------------------------------------------------------
*/

export default configSlice.reducer;